import { isNotNil, uniq, without } from 'rambda';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  type BrandKey,
  type BrandTheme,
  type EnvConfigType,
  EnvironmentType,
  type RuntimeEndpointsType,
  type RuntimeEndpointType,
} from '@/models/configuration';
import { CONFIGURATION_STORAGE_KEY } from '@/utils/constants';

export interface ConfigurationState {
  isHydrated: boolean;
  enterAsSubmit: boolean | null;
  envConfig: EnvConfigType;
  endpoint: RuntimeEndpointType;
  backupEndpoints: RuntimeEndpointsType;

  setEnvConfig: (envConfig: EnvConfigType) => void;
  setEndpoint: (endpoint: Partial<RuntimeEndpointType>) => void;
  setBackupEndpoints: (backupEndpoints: Partial<RuntimeEndpointsType>) => void;
  setBrand: (key: BrandKey, theme: BrandTheme) => void;
  setTheme: (theme: BrandTheme) => void;
  setEnterAsSubmit: (value: boolean) => void;
  setHydrated: () => void;

  removeBackupEndpoints: (type: keyof RuntimeEndpointsType, endpoint: string) => void;
}

export const useConfigurationStore = create<ConfigurationState>()(
  persist(
    immer((set, get) => {
      return {
        isHydrated: false,
        enterAsSubmit: null,
        envConfig: {
          env: EnvironmentType.LOCAL,
          brand: null,
          config: null,
          sentry: {
            dsn: '',
            enable: false,
            environment: 'local',
            release: '',
          },

          defaultEndpointUrl: [],
          defaultWebSocketEndpointUrl: [],
          defaultFileEndpointUrl: [],
          dynamicEndpoints: [],
          dynamicEndpointExternalUrl: '',
        },
        endpoint: {
          API_ENDPOINT: '',
          WEBSOCKET_ENDPOINT: '',
          FILE_ENDPOINT: '',
        },
        backupEndpoints: {
          API_ENDPOINTS: [],
          WEBSOCKET_ENDPOINTS: [],
          FILE_ENDPOINTS: [],
        } as RuntimeEndpointsType,

        setEnvConfig: (envConfig) => {
          set({ envConfig });

          set((state) => {
            state.backupEndpoints.API_ENDPOINTS.push(...envConfig.defaultEndpointUrl);
            state.backupEndpoints.WEBSOCKET_ENDPOINTS.push(...envConfig.defaultWebSocketEndpointUrl);
            state.backupEndpoints.FILE_ENDPOINTS.push(...envConfig.defaultFileEndpointUrl);
          });
        },
        setEndpoint: (endpoint) =>
          set((draft) => {
            if (!draft.endpoint) {
              draft.endpoint = {
                API_ENDPOINT: endpoint.API_ENDPOINT ?? '',
                WEBSOCKET_ENDPOINT: endpoint.WEBSOCKET_ENDPOINT ?? '',
                FILE_ENDPOINT: endpoint.FILE_ENDPOINT ?? '',
              };

              return draft;
            }

            if (isNotNil(endpoint.API_ENDPOINT)) {
              draft.endpoint.API_ENDPOINT = endpoint.API_ENDPOINT;
            }

            if (isNotNil(endpoint.WEBSOCKET_ENDPOINT)) {
              draft.endpoint.WEBSOCKET_ENDPOINT = endpoint.WEBSOCKET_ENDPOINT;
            }

            if (isNotNil(endpoint.FILE_ENDPOINT)) {
              draft.endpoint.FILE_ENDPOINT = endpoint.FILE_ENDPOINT;
            }
          }),
        setBackupEndpoints: (backupEndpoints) => {
          set((state) => {
            if (backupEndpoints.API_ENDPOINTS) {
              state.backupEndpoints.API_ENDPOINTS = uniq([
                ...state.backupEndpoints.API_ENDPOINTS,
                ...backupEndpoints.API_ENDPOINTS,
              ]);
            }
            if (backupEndpoints.WEBSOCKET_ENDPOINTS) {
              state.backupEndpoints.WEBSOCKET_ENDPOINTS = uniq([
                ...state.backupEndpoints.WEBSOCKET_ENDPOINTS,
                ...backupEndpoints.WEBSOCKET_ENDPOINTS,
              ]);
            }
            if (backupEndpoints.FILE_ENDPOINTS) {
              state.backupEndpoints.FILE_ENDPOINTS = uniq([
                ...state.backupEndpoints.FILE_ENDPOINTS,
                ...backupEndpoints.FILE_ENDPOINTS,
              ]);
            }
          });
        },
        setEnterAsSubmit: (value) => set({ enterAsSubmit: value }),
        setBrand: (key, theme) => {
          set((state) => {
            if (state.envConfig.brand) {
              state.envConfig.brand.key = key;
              state.envConfig.brand.theme = theme;
            }
          });
        },
        setTheme: (theme) => {
          set((state) => {
            if (state.envConfig.brand) {
              state.envConfig.brand.theme = theme;
            }
          });
        },
        setHydrated: () => set({ isHydrated: true }),

        removeBackupEndpoints: (type, endpoint) => {
          set((state) => {
            state.backupEndpoints[type] = without([endpoint], get().backupEndpoints[type]);

            switch (type) {
              case 'API_ENDPOINTS':
                state.envConfig.defaultEndpointUrl = without([endpoint], get().envConfig.defaultEndpointUrl);
                break;
              case 'FILE_ENDPOINTS':
                state.envConfig.defaultFileEndpointUrl = without([endpoint], get().envConfig.defaultFileEndpointUrl);
                break;
              case 'WEBSOCKET_ENDPOINTS':
                state.envConfig.defaultWebSocketEndpointUrl = without(
                  [endpoint],
                  get().envConfig.defaultWebSocketEndpointUrl,
                );
                break;
            }
          });
        },
      };
    }),
    {
      name: CONFIGURATION_STORAGE_KEY,
      version: 6,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        envConfig: state.envConfig,
        endpoint: state.endpoint,
        backupEndpoints: state.backupEndpoints,
        enterAsSubmit: state.enterAsSubmit,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
      migrate: () => ({
        envConfig: {
          env: EnvironmentType.LOCAL,
          brand: null,
          config: null,
          sentry: {
            dsn: '',
            enable: false,
            environment: 'local',
            release: '',
          },
          defaultEndpointUrl: [],
          defaultWebSocketEndpointUrl: [],
          defaultFileEndpointUrl: [],
          dynamicEndpoints: [],
          dynamicEndpointExternalUrl: '',
        },
        endpoint: {
          API_ENDPOINT: '',
          WEBSOCKET_ENDPOINT: '',
          FILE_ENDPOINT: '',
        },
        backupEndpoints: {
          API_ENDPOINTS: [],
          WEBSOCKET_ENDPOINTS: [],
          FILE_ENDPOINTS: [],
        },
        enterAsSubmit: null,
      }),
    },
  ),
);

export function promisifyConfigurationHydration() {
  return new Promise<void>((resolve) => {
    if (useConfigurationStore.getState().isHydrated) {
      resolve();
    } else {
      const unsubscribe = useConfigurationStore.subscribe(({ isHydrated }) => {
        if (isHydrated) {
          unsubscribe();
          resolve();
        }
      });
    }
  });
}
